/** Fail-closed canonicalization for post-auth navigation targets. */

export interface SafeNextOptions {
  /** Browser origin used for URL parsing and same-origin comparison. */
  readonly origin: string;
  /** Vite deployment base. A target may not escape this path. */
  readonly basePath?: string;
  /** Safe Projects fallback, already scoped to the deployment base. */
  readonly fallback: string;
  /** Additional absolute auth paths that must never become a target. */
  readonly authPaths?: readonly string[];
}

export class SafeNextConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SafeNextConfigError";
  }
}

interface SafeNextContext {
  readonly origin: string;
  readonly basePath: string;
  readonly authPaths: ReadonlySet<string>;
}

const BAD_PERCENT = /%(?![0-9a-f]{2})/iu;
const ENCODED_GLOBAL_UNSAFE = /%(?:0[0-9a-f]|1[0-9a-f]|20|5c|7f)/iu;
const ENCODED_PATH_SLASH = /%2f/iu;

function containsRawUnsafe(value: string): boolean {
  for (const character of value) {
    const code = character.charCodeAt(0);
    if (code <= 0x20 || code === 0x7f || character === "\\") return true;
  }
  return false;
}

function normalizeOrigin(raw: string): string {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw new SafeNextConfigError("origin must be an absolute URL");
  }
  if (
    (url.protocol !== "https:" && url.protocol !== "http:") ||
    url.origin === "null"
  ) {
    throw new SafeNextConfigError("origin must use http or https");
  }
  return url.origin;
}

function normalizeBasePath(raw: string | undefined, origin: string): string {
  const value = raw ?? "/";
  if (
    !value.startsWith("/") ||
    value.startsWith("//") ||
    containsRawUnsafe(value)
  ) {
    throw new SafeNextConfigError(
      "basePath must be a same-origin absolute path",
    );
  }
  const url = new URL(value, origin);
  if (url.origin !== origin || url.search || url.hash) {
    throw new SafeNextConfigError(
      "basePath must not change origin or contain query/hash",
    );
  }
  return url.pathname.endsWith("/") ? url.pathname : `${url.pathname}/`;
}

function underBase(pathname: string, basePath: string): boolean {
  if (basePath === "/") return pathname.startsWith("/");
  const baseWithoutSlash = basePath.slice(0, -1);
  return pathname === baseWithoutSlash || pathname.startsWith(basePath);
}

function baseEntry(basePath: string, name: string): string {
  return `${basePath}${name}`.replace(/\/{2,}/gu, "/");
}

function normalizeAuthPath(
  raw: string,
  origin: string,
  basePath: string,
): string {
  if (!raw.startsWith("/") || raw.startsWith("//") || containsRawUnsafe(raw)) {
    throw new SafeNextConfigError(`auth path is unsafe: ${raw}`);
  }
  const url = new URL(raw, origin);
  if (
    url.origin !== origin ||
    url.search ||
    url.hash ||
    !underBase(url.pathname, basePath)
  ) {
    throw new SafeNextConfigError(`auth path is outside basePath: ${raw}`);
  }
  return url.pathname;
}

function createContext(options: SafeNextOptions): SafeNextContext {
  const origin = normalizeOrigin(options.origin);
  const basePath = normalizeBasePath(options.basePath, origin);
  const defaults = [
    baseEntry(basePath, "login.html"),
    baseEntry(basePath, "signup.html"),
    baseEntry(basePath, "logout-recovery.html"),
  ];
  const configured = [...defaults, ...(options.authPaths ?? [])];
  const authPaths = new Set(
    configured.map((path) => normalizeAuthPath(path, origin, basePath)),
  );
  return { origin, basePath, authPaths };
}

/**
 * Decode repeatedly only for validation. The returned navigation target keeps
 * the browser's canonical encoding, while every decoded interpretation must
 * remain in-base and outside auth entries. This blocks double-encoded loops.
 */
function decodedPathInterpretations(pathname: string): string[] | null {
  const paths: string[] = [];
  let value = pathname;

  for (let depth = 0; depth < 6; depth += 1) {
    if (
      BAD_PERCENT.test(value) ||
      ENCODED_GLOBAL_UNSAFE.test(value) ||
      ENCODED_PATH_SLASH.test(value) ||
      containsRawUnsafe(value)
    ) {
      return null;
    }
    paths.push(value);

    let decoded: string;
    try {
      decoded = decodeURIComponent(value);
    } catch {
      return null;
    }
    if (decoded === value) return paths;
    value = decoded;
  }

  // More encoding layers are treated as ambiguous rather than normalized.
  return null;
}

function hasUnsafeGlobalInterpretation(raw: string): boolean {
  let value = raw;
  for (let depth = 0; depth < 6; depth += 1) {
    if (
      BAD_PERCENT.test(value) ||
      ENCODED_GLOBAL_UNSAFE.test(value) ||
      containsRawUnsafe(value)
    ) {
      return true;
    }
    let decoded: string;
    try {
      decoded = decodeURIComponent(value);
    } catch {
      return true;
    }
    if (decoded === value) return false;
    value = decoded;
  }
  return true;
}

function inspectCandidate(
  raw: unknown,
  context: SafeNextContext,
): string | null {
  if (typeof raw !== "string" || raw === "") return null;
  if (hasUnsafeGlobalInterpretation(raw)) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;

  const rawPathname = raw.split(/[?#]/u, 1)[0] ?? "";
  const interpretations = decodedPathInterpretations(rawPathname);
  if (!interpretations) return null;

  let url: URL;
  try {
    url = new URL(raw, context.origin);
  } catch {
    return null;
  }
  if (
    url.origin !== context.origin ||
    !underBase(url.pathname, context.basePath)
  )
    return null;

  for (const interpreted of interpretations) {
    if (!interpreted.startsWith("/") || interpreted.startsWith("//"))
      return null;
    let interpretedURL: URL;
    try {
      interpretedURL = new URL(interpreted, context.origin);
    } catch {
      return null;
    }
    if (
      interpretedURL.origin !== context.origin ||
      !underBase(interpretedURL.pathname, context.basePath) ||
      context.authPaths.has(interpretedURL.pathname)
    ) {
      return null;
    }
  }

  if (context.authPaths.has(url.pathname)) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Return one canonical same-origin relative path, or the validated fallback.
 * The function is idempotent and never returns an absolute/protocol-relative
 * URL.
 */
export function canonicalizeNext(
  raw: unknown,
  options: SafeNextOptions,
): string {
  const context = createContext(options);
  const fallback = inspectCandidate(options.fallback, context);
  if (!fallback) {
    throw new SafeNextConfigError(
      "fallback must be a safe non-auth path inside basePath",
    );
  }
  return inspectCandidate(raw, context) ?? fallback;
}
